// "Need help?" — opened by the "?" in the top bar and Settings › Help and feedback. Three ways, like Nursing Plan:
// the help center (its own domain, new tab), "Write to us" (support email with one-tap compose links for the common
// mail services), and "Report a problem" — a one-way note to the team: no reply comes back, and the form says so.
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '@/lib/api';
import { track } from '@/lib/analytics';
import { useSession } from '@/lib/session';
import { CopyButton, Icon, Sheet } from '@/features/settings/ui';
import { APP_BUILD, onHelpRequest, openFeedback, openSupportEmail, type HelpView } from './open';
import { composeLinks, supportMailto, trackEmailClick, SUPPORT_ADDRESS } from './support';
import '@/features/settings/settings.css'; // rows and groups shared with Settings
import './help.css';

/** The public help center (help.getbabyplan.com); VITE_HELP_URL overrides it. */
export const HELP_CENTER_URL = (import.meta.env.VITE_HELP_URL as string | undefined) ?? 'https://help.getbabyplan.com/help';

export default function HelpLayer() {
  const [view, setView] = useState<HelpView | null>(null);
  const [source, setSource] = useState('');
  useEffect(() => onHelpRequest((v, s) => { setView(v); setSource(s); }), []);
  const close = () => setView(null);
  return (
    <>
      <HelpSheet open={view === 'help'} onClose={close} />
      <EmailSheet open={view === 'email'} source={source} onClose={close} />
      <ReportSheet open={view === 'feedback'} source={source} onClose={close} />
    </>
  );
}

/** "Email support" row used on help pages: opens the email sheet. */
export function EmailRow({ source }: { source: string }) {
  const { t } = useTranslation('help');
  return (
    <li>
      <button className="set-row" type="button" onClick={() => openSupportEmail(source)}>
        <span className="set-ic" aria-hidden="true"><Icon name="mail" size={19} /></span>
        <span className="set-row-text"><span className="set-row-label">{t('sheet.email')}</span><span className="set-row-sub">{t('sheet.emailSub')}</span></span>
        <Icon name="chevron" size={16} className="set-chev" />
      </button>
    </li>
  );
}

function HelpSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t } = useTranslation('help');
  const then = (fn: () => void) => () => { onClose(); setTimeout(fn, 0); };
  return (
    <Sheet open={open} onClose={onClose} title={t('sheet.title')}>
      <ul className="set-list help-menu">
        <li>
          <a className="set-row" href={HELP_CENTER_URL} target="_blank" rel="noopener" onClick={() => { track('help_center_open', { source: 'help_sheet' }, 'help'); onClose(); }}>
            <span className="set-ic" aria-hidden="true"><Icon name="book" size={19} /></span>
            <span className="set-row-text"><span className="set-row-label">{t('sheet.center')}</span><span className="set-row-sub">{t('sheet.centerSub')}</span></span>
            <Icon name="share" size={16} className="set-chev" />
          </a>
        </li>
        <li>
          <button className="set-row" type="button" onClick={then(() => openSupportEmail('help_sheet'))}>
            <span className="set-ic" aria-hidden="true"><Icon name="mail" size={19} /></span>
            <span className="set-row-text"><span className="set-row-label">{t('sheet.email')}</span><span className="set-row-sub">{t('sheet.emailSub')}</span></span>
            <Icon name="chevron" size={16} className="set-chev" />
          </button>
        </li>
        <li>
          <button className="set-row" type="button" onClick={then(() => openFeedback('help_sheet'))}>
            <span className="set-ic" aria-hidden="true"><Icon name="wrench" size={19} /></span>
            <span className="set-row-text"><span className="set-row-label">{t('sheet.report')}</span><span className="set-row-sub">{t('sheet.reportSub')}</span></span>
            <Icon name="chevron" size={16} className="set-chev" />
          </button>
        </li>
      </ul>
    </Sheet>
  );
}

const SERVICE_STYLE: Record<string, { bg: string; fg: string; letter: string }> = {
  gmail: { bg: '#FCE8E6', fg: '#D93025', letter: 'G' },
  outlook: { bg: '#E3F0FC', fg: '#0F6CBD', letter: 'O' },
  yahoo: { bg: '#F1E8FC', fg: '#6001D2', letter: 'Y' },
  app: { bg: '#E8F3EC', fg: '#1E7A45', letter: '' },
};

function EmailSheet({ open, source, onClose }: { open: boolean; source: string; onClose: () => void }) {
  const { t } = useTranslation('help');
  const s = useSession();
  const links = composeLinks({ email: s.me?.email });
  return (
    <Sheet open={open} onClose={onClose} title={t('email.title')}>
      <div className="stack help-email">
        <p className="sheet-text">{t('email.lead')}</p>
        <div className="help-address">
          <span translate="no">{SUPPORT_ADDRESS}</span>
          <CopyButton text={SUPPORT_ADDRESS} label={t('email.copy')} copiedLabel={t('email.copied')} />
        </div>
        <p className="help-legend">{t('email.openWith')}</p>
        <div className="help-services">
          {links.map((l) => {
            const st = SERVICE_STYLE[l.id]!;
            return (
              <a
                key={l.id}
                className="help-service"
                href={l.href}
                target={l.id === 'app' ? undefined : '_blank'}
                rel="noopener"
                onClick={() => trackEmailClick(`${source || 'help'}_${l.id}`)}
              >
                <span className="help-service-ic" style={{ background: st.bg, color: st.fg }} aria-hidden="true">
                  {st.letter || <Icon name="mail" size={20} />}
                </span>
                <span>{t(`email.services.${l.id}`)}</span>
              </a>
            );
          })}
        </div>
        <p className="hint">{t('email.hint')}</p>
      </div>
    </Sheet>
  );
}

/** Problem kinds, as Nursing Plan has them; stored as `kind` in the feedback table. */
const KINDS = ['bug', 'content_error', 'confusing', 'missing', 'slow', 'idea', 'other'] as const;
const WISHES = ['growth_charts', 'memory_book', 'doctor_notes', 'shop', 'more_sounds', 'toddler', 'partner_sync', 'other_language'] as const;
/** A note about money or access needs an answer: say so before it disappears into a one-way form. */
const BILLING_RE = /\b(refund|charge[ds]?|billing|bill(ed)?|payment|paid|pay|subscription|subscribe|cancel|money|card|access|log ?in|sign ?in|password|account)\b/i;

function ReportSheet({ open, source, onClose }: { open: boolean; source: string; onClose: () => void }) {
  const { t } = useTranslation('help');
  const s = useSession();
  const [kind, setKind] = useState<(typeof KINDS)[number] | null>(null);
  const [message, setMessage] = useState('');
  const [wishes, setWishes] = useState<string[]>([]);
  const [phase, setPhase] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [problem, setProblem] = useState<'kind' | 'short' | null>(null);
  const [billingOk, setBillingOk] = useState(false);
  const from = useRef('');

  useEffect(() => {
    if (open) { from.current = location.pathname; return; }
    if (phase === 'sent') { setKind(null); setMessage(''); setWishes([]); setBillingOk(false); }
    setPhase('idle');
    setProblem(null);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const billing = BILLING_RE.test(message) && !billingOk;
  const toggleWish = (w: string) => setWishes((l) => (l.includes(w) ? l.filter((x) => x !== w) : [...l, w]));

  const send = async () => {
    if (!kind) { setProblem('kind'); return; }
    if (message.trim().length < 8 && !wishes.length) { setProblem('short'); return; }
    setProblem(null);
    setPhase('sending');
    try {
      await api('/api/feedback', { body: { kind, message: message.trim(), wishes, path: from.current, source: source || 'unknown', canReply: false, appVersion: APP_BUILD } });
      setPhase('sent');
    } catch {
      setPhase('error');
    }
  };

  const toEmail = () => { onClose(); setTimeout(() => openSupportEmail('report'), 0); };
  const signedIn = s.status === 'ready';

  return (
    <Sheet open={open} onClose={onClose} title={phase === 'sent' ? t('report.sentTitle') : t('report.title')}>
      {phase === 'sent' ? (
        <div className="stack help-sent">
          <p className="sheet-text" role="status">{t('report.sentText')}</p>
          <p className="help-oneway"><b>{t('report.noReplyBold')}</b> {t('report.noReply')}</p>
          <button className="btn btn-quiet btn-block" type="button" onClick={toEmail}>{t('report.writeSupport')}</button>
          <button className="btn btn-primary btn-block" type="button" onClick={onClose}>{t('report.done')}</button>
        </div>
      ) : !signedIn ? (
        <div className="stack">
          <p className="sheet-text">{t('report.signedOut')}</p>
          <button className="btn btn-primary btn-block" type="button" onClick={toEmail}>{t('report.writeSupport')}</button>
        </div>
      ) : (
        <form className="stack help-form" onSubmit={(e) => { e.preventDefault(); void send(); }}>
          <p className="help-oneway">{t('report.oneWay')} <button type="button" className="help-inline-link" onClick={toEmail}>{t('report.needAnswer')}</button></p>

          <fieldset className="help-field">
            <legend>{t('report.kind')}</legend>
            <div className="help-chips">
              {KINDS.map((k) => (
                <button key={k} type="button" aria-pressed={kind === k} className={`help-chip${kind === k ? ' is-on' : ''}`} data-track={`report_kind_${k}`} onClick={() => { setKind(k); setProblem(null); }}>
                  {t(`report.kinds.${k}`)}
                </button>
              ))}
            </div>
            {problem === 'kind' && <p className="error" role="alert">{t('report.pickKind')}</p>}
          </fieldset>

          <label className="help-field">
            <span className="help-legend">{t('report.text')}</span>
            <textarea rows={4} maxLength={4000} value={message} onChange={(e) => setMessage(e.target.value)} placeholder={t('report.placeholder')} />
          </label>
          {problem === 'short' && <p className="error" role="alert">{t('report.tooShort')}</p>}

          {billing && (
            <div className="help-billing" role="alert">
              <p>{t('report.billing')}</p>
              <div className="row wrap">
                <button className="btn btn-primary" type="button" onClick={toEmail}>{t('report.writeSupport')}</button>
                <button className="btn btn-text" type="button" onClick={() => setBillingOk(true)}>{t('report.sendAnyway')}</button>
              </div>
            </div>
          )}

          <fieldset className="help-field">
            <legend>{t('report.wishes')}</legend>
            <div className="help-chips">
              {WISHES.map((w) => (
                <button key={w} type="button" aria-pressed={wishes.includes(w)} className={`help-chip is-small${wishes.includes(w) ? ' is-on' : ''}`} data-track={`feedback_wish_${w}`} onClick={() => toggleWish(w)}>
                  {t(`feedback.wishList.${w}`)}
                </button>
              ))}
            </div>
          </fieldset>

          <div aria-live="polite">{phase === 'error' && <p className="error" role="alert">{t('report.sendFailed')}</p>}</div>
          <button className="btn btn-primary btn-block" type="submit" disabled={phase === 'sending' || billing}>
            {phase === 'sending' ? t('feedback.sending') : t('report.send')}
          </button>
        </form>
      )}
    </Sheet>
  );
}
